(function (global) {
  'use strict';

  var DEFAULTS = {
    base_price: 0, surface_rate: 115, extra_door: 60, partition: 45,
    weatherproof: 120, escape_large: 20, escape_each: 20, top_tray: 30,
    mat_rate: 0, cad_rate: 0, cad_hours: 0,
    ral_cage: 0, ral_door: 0, strengthen: 0,
    round_to: 5,
    min_w: 400, max_w: 1400, min_h: 400, max_h: 1200, min_d: 400, max_d: 1400,
    step: 10, def_w: 900, def_h: 700, def_d: 600,
    max_door_w: 600, min_door_w: 350, strengthen_over: 1000, twin_escape_w: 1000
  };

  // Parse a "name = value" list. Tolerant: blanks -> 0, comments ignored,
  // junk ignored, multiple pairs per line supported.
  function parseConfig(text) {
    var out = {};
    if (typeof text !== 'string') return out;
    var cleaned = text
      .replace(/\/\*[\s\S]*?\*\//g, ' ')   // /* block */
      .replace(/\/\/[^\n\r]*/g, ' ');       // // line
    var re = /([A-Za-z_][A-Za-z0-9_]*)\s*=\s*(-?\d+(?:\.\d+)?)?/g;
    var m;
    while ((m = re.exec(cleaned)) !== null) {
      var name = m[1];
      var raw = m[2];
      var val = (raw === undefined || raw === '') ? 0 : parseFloat(raw);
      out[name] = isNaN(val) ? 0 : val;
    }
    return out;
  }

  function resolveConfig(text) {
    var parsed = parseConfig(text);
    var cfg = {};
    for (var k in DEFAULTS) { cfg[k] = DEFAULTS[k]; }
    for (var j in parsed) { cfg[j] = parsed[j]; }
    return cfg;
  }

  function roundTo(n, step) {
    return step > 0 ? Math.round(n / step) * step : Math.round(n);
  }

  // cfg: resolveConfig output. c: choices object (see plan interfaces).
  function computePrice(cfg, c) {
    var surfaceArea = 2 * (c.w * c.h + c.w * c.d + c.h * c.d) / 1000000; // m^2
    var compartments = c.partitionCount + 1;

    var doorsCost = Math.max(0, c.doorCount - 1) * cfg.extra_door;
    var partitionCost = c.partitionCount * cfg.partition;
    var weatherproofCost = c.isWeatherproof ? cfg.weatherproof : 0;
    var escapeCost = 0;
    if (c.escapeChoice === 'large') { escapeCost = cfg.escape_large; }
    // 'each' is one rear door per compartment; a single-compartment cage gets
    // TWIN rear doors (forced on wide cages), so it never drops below two.
    else if (c.escapeChoice === 'each') { escapeCost = cfg.escape_each * Math.max(2, compartments); }
    // Tray price = the tray itself + coin matting priced by the roof area (w x d).
    var trayCost = c.hasTopTray ? cfg.top_tray + cfg.mat_rate * (c.w * c.d) / 1000000 : 0;
    var colourCost = (c.cageColorCustom ? cfg.ral_cage : 0) + (c.doorColorCustom ? cfg.ral_door : 0);
    // Over the threshold the build needs extra bracing (width or depth).
    var strengthenCost = (c.w > cfg.strengthen_over || c.d > cfg.strengthen_over) ? cfg.strengthen : 0;
    // Every builder cage is bespoke, so CAD design time is always charged.
    var cadCost = cfg.cad_rate * cfg.cad_hours;

    var subtotal = cfg.base_price
      + cfg.surface_rate * surfaceArea
      + doorsCost + partitionCost + weatherproofCost + escapeCost + trayCost + colourCost + strengthenCost
      + cadCost;

    return roundTo(subtotal, cfg.round_to);
  }

  // Size build rules: each typed dimension must sit inside the buildable range.
  // Returns null when fine, otherwise { dim, value, limit, type: 'big'|'small' }
  // for the first offending dimension. Blank/zero values are ignored here (the
  // separate emptiness check handles those).
  function sizeIssue(cfg, dims) {
    var rules = [
      ['width', dims.w, cfg.min_w, cfg.max_w],
      ['height', dims.h, cfg.min_h, cfg.max_h],
      ['depth', dims.d, cfg.min_d, cfg.max_d]
    ];
    for (var i = 0; i < rules.length; i++) {
      var v = rules[i][1];
      if (v > rules[i][3]) { return { dim: rules[i][0], value: v, limit: rules[i][3], type: 'big' }; }
      if (v > 0 && v < rules[i][2]) { return { dim: rules[i][0], value: v, limit: rules[i][2], type: 'small' }; }
    }
    return null;
  }

  // Door-width build rules: a single door can be at most cfg.max_door_w wide and
  // at least cfg.min_door_w. Returns null when the layout is buildable, otherwise
  // { type: 'wide'|'narrow', width } for the first offending door.
  function doorWidthIssue(cfg, c) {
    var per = c.doorsPer || 1;
    var comps = c.compartments || [];
    var i, dwmm;
    for (i = 0; i < comps.length; i++) {
      dwmm = comps[i] / per;
      if (dwmm > cfg.max_door_w) { return { type: 'wide', width: Math.round(dwmm) }; }
    }
    for (i = 0; i < comps.length; i++) {
      dwmm = comps[i] / per;
      if (dwmm < cfg.min_door_w) { return { type: 'narrow', width: Math.round(dwmm) }; }
    }
    return null;
  }

  function projectBox(w, h, d, opt) {
    opt = opt || {};
    var viewW = opt.viewW || 320;
    var viewH = opt.viewH || 260;
    var pad = opt.pad || 30;
    var angle = (opt.depthAngleDeg || 30) * Math.PI / 180;
    var depthScale = opt.depthScale != null ? opt.depthScale : 0.5;

    var dx = Math.cos(angle) * d * depthScale;
    var dy = -Math.sin(angle) * d * depthScale;
    var extentX = w + dx;
    var extentY = h + Math.abs(dy);
    var scale = Math.min((viewW - 2 * pad) / extentX, (viewH - 2 * pad) / extentY);

    var sw = w * scale, sh = h * scale, sdx = dx * scale, sdy = dy * scale;
    var originX = pad;
    var originY = viewH - pad - sh;

    var front = {
      tl: { x: originX, y: originY },
      tr: { x: originX + sw, y: originY },
      br: { x: originX + sw, y: originY + sh },
      bl: { x: originX, y: originY + sh }
    };
    var back = {
      tl: { x: front.tl.x + sdx, y: front.tl.y + sdy },
      tr: { x: front.tr.x + sdx, y: front.tr.y + sdy },
      br: { x: front.br.x + sdx, y: front.br.y + sdy },
      bl: { x: front.bl.x + sdx, y: front.bl.y + sdy }
    };
    return { front: front, back: back, scale: scale, sw: sw, sh: sh, sdx: sdx, sdy: sdy, viewW: viewW, viewH: viewH };
  }

  function r2(n) { return Math.round(n * 100) / 100; }
  function lerp(a, b, t) { return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }; }
  function svgLine(a, b, cls) {
    return '<line x1="' + r2(a.x) + '" y1="' + r2(a.y) + '" x2="' + r2(b.x) + '" y2="' + r2(b.y) + '" class="' + cls + '" />';
  }
  function svgQuad(p1, p2, p3, p4, cls) {
    return '<polygon class="' + cls + '" points="'
      + r2(p1.x) + ',' + r2(p1.y) + ' ' + r2(p2.x) + ',' + r2(p2.y) + ' '
      + r2(p3.x) + ',' + r2(p3.y) + ' ' + r2(p4.x) + ',' + r2(p4.y) + '" />';
  }

  // --- Colour helpers: derive shaded face tints from a chosen RAL hex ----------
  function clampByte(x) { return Math.max(0, Math.min(255, Math.round(x))); }
  function hexToRgb(hex) {
    if (typeof hex !== 'string') { return null; }
    var h = hex.replace('#', '');
    if (h.length === 3) { h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]; }
    if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) { return null; }
    var n = parseInt(h, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }
  function toHex(r, g, b) {
    return '#' + [r, g, b].map(function (v) {
      var s = clampByte(v).toString(16);
      return s.length < 2 ? '0' + s : s;
    }).join('');
  }
  // shade(hex, +pct) lightens towards white, shade(hex, -pct) darkens towards black.
  function shade(hex, pct) {
    var c = hexToRgb(hex);
    if (!c) { return hex; }
    var target = pct < 0 ? 0 : 255;
    var p = Math.abs(pct) / 100;
    return toHex(c.r + (target - c.r) * p, c.g + (target - c.g) * p, c.b + (target - c.b) * p);
  }
  // Build the inline CSS custom properties that recolour the illustration. Body
  // colour shades the top/side/front faces so the box still reads as 3D; door
  // colour paints the doors and welded mesh.
  function colourStyleAttr(bodyHex, doorHex) {
    var vars = [];
    if (hexToRgb(bodyHex)) {
      vars.push('--cc-top:' + shade(bodyHex, 16));
      vars.push('--cc-side:' + shade(bodyHex, -24));
      vars.push('--cc-frame:' + shade(bodyHex, 4));
      vars.push('--cc-part:' + shade(bodyHex, 2));
      vars.push('--cc-lip:' + shade(bodyHex, -14));
      vars.push('--cc-edge:' + shade(bodyHex, -42));
    }
    var dc = hexToRgb(doorHex);
    if (dc) {
      // As on the real door, the welded mesh wires are painted the SAME colour as
      // the door band; they read against the lighter cage interior behind them.
      vars.push('--cc-door:' + doorHex);                  // band + solid weatherproof door
      vars.push('--cc-mesh:' + doorHex);                  // mesh wires (open model)
      vars.push('--cc-doorframe:' + shade(doorHex, -20)); // door perimeter frame
    }
    return vars.length ? ' style="' + vars.join(';') + '"' : '';
  }

  function wireframeLabel(c) {
    var comps = (c.compartments && c.compartments.length) ? c.compartments.length : ((c.partitionCount || 0) + 1);
    var doorsPer = c.doorsPer != null ? c.doorsPer : 1;
    var doorCount = c.doorCount != null ? c.doorCount : doorsPer * comps;
    var partitions = comps - 1;
    var s = 'Illustration of a ' + c.w + ' by ' + c.h + ' by ' + c.d + ' millimetre cage with '
      + comps + ' compartment' + (comps === 1 ? '' : 's')
      + ', ' + doorCount + ' door' + (doorCount === 1 ? '' : 's')
      + ', ' + partitions + ' partition' + (partitions === 1 ? '' : 's')
      + (c.isWeatherproof ? ', weatherproof panels' : ', mesh panels');
    if (c.escapeChoice === 'large') { s += ', one large rear escape door'; }
    else if (c.escapeChoice === 'each') { s += comps > 1 ? ', ' + comps + ' individual rear escape doors' : ', twin rear escape doors'; }
    else { s += ', no escape door'; }
    if (c.hasTopTray) { s += ', and a top tray'; }
    if (c.hingeChoice === 'left') { s += '. All doors are hinged on the left'; }
    else if (c.hingeChoice === 'right') { s += '. All doors are hinged on the right'; }
    else if (c.hingeChoice === 'centre') { s += '. Doors are hinged at the centre and open outwards'; }
    if (c.cageColor && c.cageColorHex) { s += '. Body finished in ' + c.cageColor; }
    if (c.doorColor && c.doorColorHex) { s += (c.cageColor && c.cageColorHex ? ', doors in ' : '. Doors finished in ') + c.doorColor; }
    return s.replace(/"/g, '');
  }

  var MESH_CELL_MM = 35; // real pitch of the welded mesh holes
  var BAND_MM = 30;      // real width of the solid door band around the mesh

  // Which side a door hangs from. 'left'/'right' hang EVERY door on that side;
  // 'mirror' (a standard) hinges doors at the outside edges so they open
  // outwards from the centre: left-half doors hinge left, right-half right.
  // 'centre' (the other standard) is that flipped: doors hinge on their inner
  // edges, against the middle mullion or partition post, and swing outwards.
  function hingeSide(choice, di, doorsPer, ci, compCount) {
    if (choice === 'left') { return true; }
    if (choice === 'right') { return false; }
    var outer = doorsPer > 1 ? (di < doorsPer / 2) : (compCount === 1 || ci < compCount / 2);
    // a lone door has no centre to hinge against, so it stays hung as standard
    if (choice === 'centre' && doorsPer * compCount > 1) { return !outer; }
    return outer;
  }

  function doorSvg(dx0, dy0, dw, dh, hingeLeft, weatherproof, scale) {
    var out = '';
    var latches = '';
    var bw = Math.max(2.5, BAND_MM * scale);  // solid band width (real mm)
    var knobR = Math.max(2.2, 20 * scale);    // quarter-turn knob, fixed real size
    if (weatherproof) {
      // Weatherproof: a solid painted door whose TOP section is the same welded
      // mesh as the open cage (see-through, whole holes, fixed real pitch); the
      // rest of the door is solid.
      out += '<rect class="cage-cad__door" data-door x="' + r2(dx0) + '" y="' + r2(dy0)
        + '" width="' + r2(dw) + '" height="' + r2(dh) + '" rx="2" />';
      // The mesh window sits high on the door but is inset from the latch edge,
      // leaving solid door beside it for the top knob (which sits LEVEL with the
      // window, as on the real door), and a wider-than-band margin either side.
      var latchMargin = bw + 2.5 + knobR + 3;
      var hingeMargin = bw + 4;
      var wmx = dx0 + (hingeLeft ? hingeMargin : latchMargin);
      var wmw = Math.max(8, dw - latchMargin - hingeMargin);
      var wmy = dy0 + Math.max(bw, dh * 0.07);
      var wmh = Math.max(8, dh * 0.22);
      var wcell = Math.max(4, MESH_CELL_MM * scale);
      out += '<rect class="cage-cad__door-interior" x="' + r2(wmx) + '" y="' + r2(wmy)
        + '" width="' + r2(wmw) + '" height="' + r2(wmh) + '" />';
      out += meshGrid(wmx, wmy, wmw, wmh,
        Math.max(3, Math.round(wmw / wcell)), Math.max(2, Math.round(wmh / wcell)));
      var latchX = hingeLeft ? dx0 + dw - bw - 2.5 : dx0 + bw + 2.5;
      [0.2, 0.8].forEach(function (t) {
        latches += knobSvg(latchX, dy0 + dh * t, knobR);
      });
    } else {
      // Open cage: a welded mesh door. A solid coloured band runs around the
      // outside; inside it sits the see-through mesh window. Band width and mesh
      // pitch are fixed real sizes (mm), so resizing the cage changes the NUMBER
      // of holes, never their size.
      out += '<rect class="cage-cad__door-band" data-door x="' + r2(dx0) + '" y="' + r2(dy0)
        + '" width="' + r2(dw) + '" height="' + r2(dh) + '" rx="2" />';
      var mx = dx0 + bw, my = dy0 + bw, mw = dw - 2 * bw, mh = dh - 2 * bw;
      out += '<rect class="cage-cad__door-interior" x="' + r2(mx) + '" y="' + r2(my)
        + '" width="' + r2(mw) + '" height="' + r2(mh) + '" />';
      var cellPx = Math.max(4, MESH_CELL_MM * scale);
      var cols = Math.max(3, Math.round(mw / cellPx));
      var rows = Math.max(3, Math.round(mh / cellPx));
      var mcw = mw / cols, mch = mh / rows;
      out += meshGrid(mx, my, mw, mh, cols, rows);

      // Latch knobs mount on a solid patch of WHOLE mesh cells (2 x 2), flush
      // with the latch-side band. Snapping the patch to the grid means the
      // squares around it always stay complete, at any cage size.
      var kr = Math.min(knobR, Math.min(mcw, mch) * 0.85);
      var c0 = hingeLeft ? cols - 2 : 0;
      [0.2, 0.8].forEach(function (t) {
        var r0 = Math.round((dy0 + dh * t - my) / mch) - 1;
        if (r0 < 0) { r0 = 0; }
        if (r0 > rows - 2) { r0 = rows - 2; }
        var px0 = mx + c0 * mcw, py0 = my + r0 * mch;
        latches += '<rect class="cage-cad__latch-plate" x="' + r2(px0) + '" y="' + r2(py0)
          + '" width="' + r2(2 * mcw) + '" height="' + r2(2 * mch) + '" />';
        latches += knobSvg(px0 + mcw, py0 + mch, kr);
      });
    }
    out += '<rect class="cage-cad__door-frame" x="' + r2(dx0) + '" y="' + r2(dy0)
      + '" width="' + r2(dw) + '" height="' + r2(dh) + '" rx="2" />';

    // HINGES: chunky black tabs straddling the door's outer edge onto the cage
    // frame, each with a metal pin.
    var hingeX = hingeLeft ? dx0 + 1 : dx0 + dw - 1;
    [0.14, 0.86].forEach(function (t) {
      var hy = dy0 + dh * t;
      out += '<rect class="cage-cad__hinge" x="' + r2(hingeX - 3.5) + '" y="' + r2(hy - 4.5) + '" width="7" height="9" rx="1.5" />';
      out += '<circle class="cage-cad__hinge-pin" cx="' + r2(hingeX) + '" cy="' + r2(hy) + '" r="1.1" />';
    });

    out += latches;
    return out;
  }

  // An X-shaped wing thumb screw, as fitted top and bottom of each partition post.
  function thumbScrew(cx, cy) {
    return '<g class="cage-cad__thumb" transform="translate(' + r2(cx) + ' ' + r2(cy) + ') rotate(45)">'
      + '<rect class="cage-cad__thumb-bar" x="-4.2" y="-1.2" width="8.4" height="2.4" rx="1.2" />'
      + '<rect class="cage-cad__thumb-bar" x="-1.2" y="-4.2" width="2.4" height="8.4" rx="1.2" />'
      + '<circle class="cage-cad__thumb-hub" r="1.2" />'
      + '</g>';
  }

  // A welded square-mesh grid filling (x,y,w,h) with WHOLE holes only, cols x rows
  // cells. Only interior wires are drawn: the outermost holes sit directly against
  // the door band with no wire border around the mesh window, as on the real door.
  function meshGrid(x, y, w, h, cols, rows) {
    var cw = w / cols, ch = h / rows;
    var d = '', i;
    for (i = 1; i < cols; i++) { var gx = x + i * cw; d += 'M' + r2(gx) + ' ' + r2(y) + 'V' + r2(y + h); }
    for (i = 1; i < rows; i++) { var gy = y + i * ch; d += 'M' + r2(x) + ' ' + r2(gy) + 'H' + r2(x + w); }
    return '<path class="cage-cad__mesh-line" d="' + d + '" fill="none" />';
  }

  // Round black quarter-turn knob with a bright slotted metal face.
  function knobSvg(cx, cy, r) {
    var f = r * 0.57;
    return '<circle class="cage-cad__latch-body" cx="' + r2(cx) + '" cy="' + r2(cy) + '" r="' + r2(r) + '" />'
      + '<circle class="cage-cad__latch-face" cx="' + r2(cx) + '" cy="' + r2(cy) + '" r="' + r2(f) + '" />'
      + '<rect class="cage-cad__latch-slot" x="' + r2(cx - f * 0.85) + '" y="' + r2(cy - f * 0.26) + '" width="' + r2(f * 1.7) + '" height="' + r2(f * 0.52) + '" rx="' + r2(f * 0.26) + '" />';
  }

  // A rectangular ventilation grille (dark holes behind a grid of bars) with
  // cols x rows WHOLE cells, so no hole is ever cut, matching the welded mesh.
  function ventGrid(x, y, w, h, cols, rows) {
    var s = '<rect class="cage-cad__vent-bg" x="' + r2(x) + '" y="' + r2(y) + '" width="' + r2(w) + '" height="' + r2(h) + '" />';
    var cw = w / cols, ch = h / rows, i;
    for (i = 1; i < cols; i++) {
      s += '<line class="cage-cad__vent-bar" x1="' + r2(x + i * cw) + '" y1="' + r2(y) + '" x2="' + r2(x + i * cw) + '" y2="' + r2(y + h) + '" />';
    }
    for (i = 1; i < rows; i++) {
      s += '<line class="cage-cad__vent-bar" x1="' + r2(x) + '" y1="' + r2(y + i * ch) + '" x2="' + r2(x + w) + '" y2="' + r2(y + i * ch) + '" />';
    }
    s += '<rect class="cage-cad__vent-frame" x="' + r2(x) + '" y="' + r2(y) + '" width="' + r2(w) + '" height="' + r2(h) + '" />';
    return s;
  }

  // Product-style illustration of the cage. Partitions create compartments, sized
  // by c.compartments (mm). Each compartment holds c.doorsPer doors, so a divider
  // post can never fall in the middle of a door.
  function buildWireframeSvg(c) {
    var comps = (c.compartments && c.compartments.length) ? c.compartments : [c.w || 1];
    var doorsPer = c.doorsPer || 1;
    var total = 0;
    for (var ti = 0; ti < comps.length; ti++) { total += comps[ti]; }
    if (!total) { total = 1; }

    var g = projectBox(c.w, c.h, c.d, {});
    var f = g.front, b = g.back;
    var ft = Math.max(4, Math.min(10, g.sw * 0.035)); // aluminium frame thickness
    var mode = c.isWeatherproof ? 'is-weatherproof' : 'is-mesh';
    var parts = [];

    parts.push('<defs>'
      + '<linearGradient id="cageInterior" x1="0" y1="0" x2="0" y2="1">'
      +   '<stop offset="0" stop-color="#8f9498" /><stop offset="0.6" stop-color="#6f7478" /><stop offset="1" stop-color="#26292c" />'
      + '</linearGradient>'
      + '<pattern id="coinMat" width="7" height="7" patternUnits="userSpaceOnUse"><circle class="cage-cad__coin" cx="3.5" cy="3.5" r="1.9" /></pattern>'
      + '</defs>');

    // Open/mesh cages have a flush top. The weatherproof roof overhangs the front
    // and is drawn later (after the doors) so its lip sits proud over them.
    if (!c.isWeatherproof) {
      parts.push(svgQuad(f.tl, f.tr, b.tr, b.tl, 'cage-cad__top'));
    }

    // SIDE face. Open/mesh cages have a MESH ventilation strip along the top of the
    // side panel plus a recessed oval handle. Weatherproof side panels are solid
    // (no mesh). Decorations use a transform (0..100 -> the parallelogram) so they
    // follow the panel's perspective.
    parts.push(svgQuad(f.tr, b.tr, b.br, f.br, 'cage-cad__side'));
    if (!c.isWeatherproof) {
      var sideT = 'matrix(' + r2(g.sdx / 100) + ',' + r2(g.sdy / 100) + ',0,' + r2(g.sh / 100) + ',' + r2(f.tr.x) + ',' + r2(f.tr.y) + ')';
      parts.push('<g transform="' + sideT + '">' + ventGrid(9, 8, 82, 14, 16, 3) + '</g>'); // mesh vent strip along the top

      // Recessed lifting handle: drawn at a FIXED shape/size (tracks the box height,
      // oriented along the depth axis) so it never stretches as the box gets deeper.
      var Dlen = Math.sqrt(g.sdx * g.sdx + g.sdy * g.sdy) || 1;
      var hW = 0.16 * g.sh, hH = 0.10 * g.sh;          // on-screen size, independent of depth
      var hcx = f.tr.x + 0.45 * g.sdx;                  // ~45% along the depth
      var hcy = f.tr.y + 0.45 * g.sdy + 0.50 * g.sh;    // vertical centre of the side panel
      var hT = 'matrix(' + r2(g.sdx / Dlen) + ',' + r2(g.sdy / Dlen) + ',0,1,' + r2(hcx) + ',' + r2(hcy) + ')';
      parts.push('<g transform="' + hT + '">'
        + '<rect class="cage-cad__handle" x="' + r2(-hW / 2) + '" y="' + r2(-hH / 2) + '" width="' + r2(hW) + '" height="' + r2(hH) + '" rx="' + r2(hH / 2) + '" />'
        + '<rect class="cage-cad__handle-slot" x="' + r2(-hW / 2 + 3) + '" y="' + r2(-hH / 2 + 2.5) + '" width="' + r2(hW - 6) + '" height="' + r2(hH - 5) + '" rx="' + r2(Math.max(0.5, (hH - 5) / 2)) + '" />'
        + '</g>');
    }

    // visible connecting edges (silver)
    parts.push(svgLine(f.tl, b.tl, 'cage-cad__edge'));
    parts.push(svgLine(f.tr, b.tr, 'cage-cad__edge'));
    parts.push(svgLine(f.br, b.br, 'cage-cad__edge'));

    // FRONT frame (silver)
    parts.push(svgQuad(f.tl, f.tr, f.br, f.bl, 'cage-cad__frame'));

    // inner area
    var ix = f.tl.x + ft, iy = f.tl.y + ft, iw = g.sw - 2 * ft, ih = g.sh - 2 * ft;
    var dy0 = iy + 1.5, dh = ih - 3;

    // PARTITION posts + top dividers FIRST: door hinges can bolt onto a post,
    // so the doors must paint over the posts, never the other way round. (The
    // posts' thumb screws stand proud, so THEY draw after the doors, below.)
    var cum = 0;
    for (var pi = 0; pi < comps.length - 1; pi++) {
      cum += comps[pi];
      var px = ix + iw * (cum / total);
      parts.push(svgLine({ x: px, y: f.tl.y }, { x: px + g.sdx, y: f.tl.y + g.sdy }, 'cage-cad__partition-top'));
      parts.push('<rect class="cage-cad__partition" data-partition x="' + r2(px - ft / 2) + '" y="' + r2(iy) + '" width="' + r2(ft) + '" height="' + r2(ih) + '" />');
    }

    // COMPARTMENTS: draw doorsPer doors inside each, sized by compartment width.
    // Doors keep a wide clearance at INTERNAL boundaries so the partition post
    // and its thumb screws stay clearly visible between them; outer edges stay
    // snug to the frame, and twin doors within one compartment meet closely.
    var postGap = ft / 2 + 3;
    cum = 0;
    for (var ci = 0; ci < comps.length; ci++) {
      var cx0 = ix + iw * (cum / total);
      cum += comps[ci];
      var cx1 = ix + iw * (cum / total);
      var leftIn = ci > 0 ? postGap : 1.5;
      var rightIn = ci < comps.length - 1 ? postGap : 1.5;
      var ax0 = cx0 + leftIn;
      var aw = (cx1 - cx0) - leftIn - rightIn;
      for (var di = 0; di < doorsPer; di++) {
        var ddx0 = ax0 + (aw * di / doorsPer) + (di > 0 ? 1.5 : 0);
        var ddw = aw / doorsPer - (doorsPer > 1 ? 1.5 : 0);
        var hingeLeft = hingeSide(c.hingeChoice, di, doorsPer, ci, comps.length);
        parts.push(doorSvg(ddx0, dy0, ddw, dh, hingeLeft, c.isWeatherproof, g.scale));
      }
    }

    // Partition thumb screws stand proud of the posts, in front of the door
    // edges, so they draw after the doors.
    cum = 0;
    for (var ts = 0; ts < comps.length - 1; ts++) {
      cum += comps[ts];
      var tpx = ix + iw * (cum / total);
      parts.push(thumbScrew(tpx, iy + 6));
      parts.push(thumbScrew(tpx, iy + ih - 6));
    }

    // ROOF: weatherproof only. A top slab that overhangs the front (and sides),
    // drawn over the door/frame tops so the front lip sits proud above the doors.
    var badgeY = f.tl.y + ft + 2;
    if (c.isWeatherproof) {
      var ovF = 0.16, ovS = g.sw * 0.02, lipT = Math.max(4, g.sh * 0.02);
      var fwdx = -g.sdx * ovF, fwdy = -g.sdy * ovF; // forward = towards the viewer
      var rfl = { x: f.tl.x - ovS + fwdx, y: f.tl.y + fwdy };
      var rfr = { x: f.tr.x + ovS + fwdx, y: f.tr.y + fwdy };
      var rbl = { x: b.tl.x - ovS, y: b.tl.y };
      var rbr = { x: b.tr.x + ovS, y: b.tr.y };
      parts.push(svgQuad(rfl, rfr, rbr, rbl, 'cage-cad__top'));                                            // roof top
      parts.push(svgQuad(rfl, rfr, { x: rfr.x, y: rfr.y + lipT }, { x: rfl.x, y: rfl.y + lipT }, 'cage-cad__lip')); // front fascia
      badgeY = rfr.y + lipT + 4;
    }

    // small brand badge, top-right of the door face
    parts.push('<rect class="cage-cad__badge" x="' + r2(f.tr.x - ft - 16) + '" y="' + r2(badgeY) + '" width="13" height="6" rx="1" />');

    // REAR escape doors: shown as a small "Rear view" elevation UNDER the main
    // drawing (dashed outlines ghosted over the front view read as clutter).
    // The mini panel repeats the compartment layout, so each numbered escape
    // door lines up with its compartment in the front view.
    var hasEscape = c.escapeChoice === 'large' || c.escapeChoice === 'each';
    if (hasEscape) {
      var rs = Math.min(150 / c.w, 54 / c.h);
      var rw = c.w * rs, rh = c.h * rs;
      var rx0 = 30, ry0 = g.viewH + 10 + (54 - rh) / 2;
      parts.push('<rect class="cage-cad__rear-body" x="' + r2(rx0) + '" y="' + r2(ry0)
        + '" width="' + r2(rw) + '" height="' + r2(rh) + '" rx="2" />');
      var acc = 0;
      for (var rd = 0; rd < comps.length - 1; rd++) {
        acc += comps[rd];
        var rxp = rx0 + rw * (acc / total);
        parts.push(svgLine({ x: rxp, y: ry0 }, { x: rxp, y: ry0 + rh }, 'cage-cad__rear-divider'));
      }
      if (c.escapeChoice === 'large') {
        parts.push('<rect class="cage-cad__escape" data-escape="large" x="' + r2(rx0 + 4) + '" y="' + r2(ry0 + 4)
          + '" width="' + r2(rw - 8) + '" height="' + r2(rh - 8) + '" rx="2" />');
      } else if (comps.length === 1) {
        // Single wide compartment: TWIN rear doors, splitting the rear in half.
        [0, 0.5].forEach(function (tf) {
          parts.push('<rect class="cage-cad__escape" data-escape="each" x="' + r2(rx0 + rw * tf + 4) + '" y="' + r2(ry0 + 4)
            + '" width="' + r2(rw * 0.5 - 8) + '" height="' + r2(rh - 8) + '" rx="2" />');
        });
      } else {
        var eacc = 0;
        for (var ek = 0; ek < comps.length; ek++) {
          var efs = eacc / total; eacc += comps[ek]; var efe = eacc / total;
          var ex0 = rx0 + rw * efs + 4;
          var ew = rw * (efe - efs) - 8;
          parts.push('<rect class="cage-cad__escape" data-escape="each" x="' + r2(ex0) + '" y="' + r2(ry0 + 4)
            + '" width="' + r2(ew) + '" height="' + r2(rh - 8) + '" rx="2" />');
          parts.push('<text class="cage-cad__escape-num" x="' + r2(ex0 + ew / 2) + '" y="' + r2(ry0 + rh / 2 + 3.5) + '">' + (ek + 1) + '</text>');
        }
      }
      var rearLabel = 'Rear view: escape doors';
      if (c.escapeChoice === 'large') { rearLabel = 'Rear view: escape door'; }
      else if (comps.length === 1) { rearLabel = 'Rear view: twin escape doors'; }
      parts.push('<text class="cage-cad__rear-label" x="' + r2(rx0 + rw + 12) + '" y="' + r2(ry0 + rh / 2 + 3) + '">' + rearLabel + '</text>');
    }

    // TOP TRAY (open cages only): a shallow tray covering most of the top, with a
    // textured anti-slip mat and a raised lip standing around the whole perimeter.
    if (c.hasTopTray && !c.isWeatherproof) {
      // Full-size tray: TALL lips on the back and both sides, a LOW lip at the front
      // (the tall side lips chamfer down to the front), with rubber coin matting that
      // sits only on the flat top panel, inside the lips.
      var lipT = Math.max(6, g.sh * 0.08);  // tall (back + sides)
      var lipF = Math.max(2, g.sh * 0.025); // low (front)
      var chV = 0.16;                       // chamfer length along the sides at the front
      var tp = function (u, v) { return { x: f.tl.x + u * g.sw + v * g.sdx, y: f.tl.y + v * g.sdy }; };
      var up = function (p, h) { return { x: p.x, y: p.y - h }; };
      var poly = function (arr) { return arr.map(function (p) { return r2(p.x) + ',' + r2(p.y); }).join(' '); };
      var nL = tp(0, 0), nR = tp(1, 0), fR = tp(1, 1), fL = tp(0, 1);
      var cL = tp(0, chV), cR = tp(1, chV); // where the side chamfer meets full height

      // tall back lip + tall LEFT side lip sit BEHIND the coin panel
      parts.push(svgQuad(fL, fR, up(fR, lipT), up(fL, lipT), 'cage-cad__tray-rim'));
      parts.push('<polygon class="cage-cad__tray-rim" points="' + poly([nL, fL, up(fL, lipT), up(cL, lipT), up(nL, lipF)]) + '" />');

      // dark tray floor (full), then coin studs IN the top-face perspective, filling
      // the whole panel up to the upright edges of the lips
      parts.push('<polygon class="cage-cad__tray-mat" data-top-tray points="' + poly([nL, nR, fR, fL]) + '" />');
      var topT = 'matrix(' + r2(g.sw / 100) + ',0,' + r2(g.sdx / 100) + ',' + r2(g.sdy / 100) + ',' + r2(f.tl.x) + ',' + r2(f.tl.y) + ')';
      parts.push('<g transform="' + topT + '"><rect x="0" y="0" width="100" height="100" fill="url(#coinMat)" /></g>');

      // RIGHT side lip + low front lip sit IN FRONT of the coin panel (in this
      // projection the right lip overlaps the top surface, so it must be drawn last
      // or the coins would show through it).
      parts.push('<polygon class="cage-cad__tray-rim" points="' + poly([nR, fR, up(fR, lipT), up(cR, lipT), up(nR, lipF)]) + '" />');
      parts.push(svgQuad(nL, nR, up(nR, lipF), up(nL, lipF), 'cage-cad__tray-lip'));

      // rim top edges
      parts.push(svgLine(up(fL, lipT), up(fR, lipT), 'cage-cad__tray-edge')); // back
      parts.push(svgLine(up(cL, lipT), up(fL, lipT), 'cage-cad__tray-edge')); // left tall
      parts.push(svgLine(up(cR, lipT), up(fR, lipT), 'cage-cad__tray-edge')); // right tall
      parts.push(svgLine(up(nL, lipF), up(cL, lipT), 'cage-cad__tray-edge')); // left chamfer
      parts.push(svgLine(up(nR, lipF), up(cR, lipT), 'cage-cad__tray-edge')); // right chamfer
      parts.push(svgLine(up(nL, lipF), up(nR, lipF), 'cage-cad__tray-edge')); // front
    }

    return '<svg class="cage-cad ' + mode + '"' + colourStyleAttr(c.cageColorHex, c.doorColorHex)
      + ' viewBox="0 0 ' + g.viewW + ' ' + (g.viewH + (hasEscape ? 74 : 0))
      + '" role="img" aria-label="' + wireframeLabel(c) + '" xmlns="http://www.w3.org/2000/svg">'
      + parts.join('') + '</svg>';
  }

  var CURRENCY = '£'; // pound sign; overridden per section by data-currency

  function formatMoney(n) {
    try { return Math.round(n).toLocaleString('en-GB'); }
    catch (e) { return String(Math.round(n)); }
  }

  function intVal(el, def) {
    if (!el) { return def; }
    var v = parseInt(el.value, 10);
    return isNaN(v) ? def : v;
  }
  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

  function readChoices(root, cfg) {
    var q = function (sel) { return root.querySelector(sel); };
    var wp = q('[data-cage-input="weatherproof"]');
    var tray = q('[data-cage-input="toptray"]');
    var escSel = q('[data-cage-input="escape"]');
    var customEl = q('[data-cage-input="customwidths"]');
    var custom = !!(customEl && customEl.checked);

    // Finish colours. The standard finish (the option marked data-standard, e.g.
    // RAL 7016 cage with RAL 9005 doors) is preselected and included in the price;
    // choosing any other RAL adds that colour charge. The drawing is always
    // painted with the selected colours.
    var cageSel = q('[data-cage-input="cagecolour"]');
    var doorSel = q('[data-cage-input="doorcolour"]');
    var cageCustom = isCustomColour(cageSel);
    var doorCustom = isCustomColour(doorSel);

    var partSel = q('[data-cage-input="partitions"]');
    var partitionCount = intVal(partSel, 0);
    var compartmentCount = partitionCount + 1;
    // 1 door per compartment; a single-compartment cage may have 1 or 2.
    var doorsPer = (partitionCount === 0) ? intVal(q('[data-cage-input="doorsper"]'), 1) : 1;

    var h = clamp(intVal(q('[data-cage-input="h"]'), cfg.def_h), cfg.min_h, cfg.max_h);
    var d = clamp(intVal(q('[data-cage-input="d"]'), cfg.def_d), cfg.min_d, cfg.max_d);

    // Compartment widths. Custom mode reads one input per compartment; otherwise
    // the overall width is split evenly.
    var compartments = [];
    if (custom) {
      var cws = root.querySelectorAll('[data-cage-cw]');
      for (var i = 0; i < cws.length; i++) { compartments.push(Math.max(1, intVal(cws[i], 0))); }
    }
    if (compartments.length !== compartmentCount) {
      var totalW = clamp(intVal(q('[data-cage-input="w"]'), cfg.def_w), cfg.min_w, cfg.max_w);
      compartments = [];
      for (var k = 0; k < compartmentCount; k++) { compartments.push(totalW / compartmentCount); }
    }
    var w = 0;
    for (var j = 0; j < compartments.length; j++) { w += compartments[j]; }
    w = Math.round(w);

    return {
      w: w, h: h, d: d,
      compartments: compartments,
      compartmentCount: compartmentCount,
      partitionCount: partitionCount,
      doorsPer: doorsPer,
      doorCount: doorsPer * compartmentCount,
      customWidths: custom,
      isWeatherproof: !!(wp && wp.checked),
      hasTopTray: !(wp && wp.checked) && !!(tray && tray.checked), // no tray on weatherproof
      escapeChoice: escSel ? escSel.value : 'none',
      hingeChoice: (function (hs) { return hs ? hs.value : 'mirror'; })(q('[data-cage-input="hinge"]')),
      needsStrengthen: w > cfg.strengthen_over || d > cfg.strengthen_over,
      cageColor: selText(cageSel),
      doorColor: selText(doorSel),
      cageColorHex: selHex(cageSel),
      doorColorHex: selHex(doorSel),
      cageColorCustom: cageCustom,
      doorColorCustom: doorCustom,
      cageColorIsDefault: isDefaultOption(cageSel),
      doorColorIsDefault: isDefaultOption(doorSel),
      partitionsIsDefault: partitionCount === defaultSelectNum(partSel, 0),
      cageSwatch: selHex(cageSel),
      doorSwatch: selHex(doorSel)
    };
  }

  // True when the select still sits on the option marked selected in the HTML,
  // so share links can leave the preselected colours out.
  function isDefaultOption(sel) {
    var o = selOpt(sel);
    return o ? !!o.defaultSelected : true;
  }

  // Numeric value of the option a select opens on (the one marked selected in
  // the HTML, or the first option). Share links omit values still on it.
  function defaultSelectNum(sel, fallback) {
    if (!sel || !sel.options || !sel.options.length) { return fallback; }
    var def = null;
    for (var i = 0; i < sel.options.length; i++) {
      if (sel.options[i].defaultSelected) { def = sel.options[i]; break; }
    }
    var n = parseInt((def || sel.options[0]).value, 10);
    return isNaN(n) ? fallback : n;
  }

  // --- Shareable build links ------------------------------------------------
  // The whole configuration round-trips through a short query string so a
  // build can be copied, shared, and restored. Values matching the defaults
  // are left out to keep links short; decoding validates everything against
  // the price file and silently drops anything out of range or unrecognised.

  function ralNum(text) {
    var m = /RAL\s*(\d{3,4})/.exec(text || '');
    return m ? m[1] : '';
  }

  function encodeChoices(cfg, c) {
    var parts = [];
    function add(k, v) { parts.push(k + '=' + v); }
    if (c.customWidths && c.compartments && c.compartments.length > 1) {
      var ws = [];
      for (var i = 0; i < c.compartments.length; i++) { ws.push(Math.round(c.compartments[i])); }
      add('cw', ws.join('-'));
    } else {
      if (c.w !== cfg.def_w) { add('w', c.w); }
      if (!c.partitionsIsDefault) { add('p', c.partitionCount); }
    }
    if (c.h !== cfg.def_h) { add('h', c.h); }
    if (c.d !== cfg.def_d) { add('d', c.d); }
    if (c.doorsPer > 1) { add('dpc', c.doorsPer); }
    if (c.hingeChoice && c.hingeChoice !== 'mirror') { add('hng', c.hingeChoice); }
    if (c.escapeChoice && c.escapeChoice !== 'none') { add('esc', c.escapeChoice); }
    if (c.isWeatherproof) { add('wp', 1); }
    if (c.hasTopTray) { add('tray', 1); }
    var cn = ralNum(c.cageColor);
    var dn = ralNum(c.doorColor);
    if (cn && !c.cageColorIsDefault) { add('cc', cn); }
    if (dn && !c.doorColorIsDefault) { add('dc', dn); }
    return parts.join('&');
  }

  function decodeParams(search, cfg) {
    var out = {};
    if (typeof search !== 'string' || !search) { return out; }
    var q = search.charAt(0) === '?' ? search.slice(1) : search;
    var raw = {};
    var pairs = q.split('&');
    for (var i = 0; i < pairs.length; i++) {
      var eq = pairs[i].indexOf('=');
      if (eq < 1) { continue; }
      var k = pairs[i].slice(0, eq);
      var v;
      try { v = decodeURIComponent(pairs[i].slice(eq + 1)); } catch (e) { continue; }
      raw[k] = v;
    }
    function dim(v, lo, hi) {
      if (!/^\d+$/.test(v || '')) { return null; }
      var n = parseInt(v, 10);
      return (n >= lo && n <= hi) ? n : null;
    }
    var n;
    if ((n = dim(raw.w, cfg.min_w, cfg.max_w)) !== null) { out.w = n; }
    if ((n = dim(raw.h, cfg.min_h, cfg.max_h)) !== null) { out.h = n; }
    if ((n = dim(raw.d, cfg.min_d, cfg.max_d)) !== null) { out.d = n; }
    if ((n = dim(raw.p, 0, 6)) !== null) { out.partitions = n; }
    if ((n = dim(raw.dpc, 1, 4)) !== null) { out.doorsPer = n; }
    if (raw.hng === 'left' || raw.hng === 'right' || raw.hng === 'mirror' || raw.hng === 'centre') { out.hinge = raw.hng; }
    if (raw.esc === 'large' || raw.esc === 'each' || raw.esc === 'none') { out.escape = raw.esc; }
    if (raw.wp === '1') { out.weatherproof = true; }
    if (raw.tray === '1') { out.topTray = true; }
    if (/^\d{3,4}$/.test(raw.cc || '')) { out.cage = raw.cc; }
    if (/^\d{3,4}$/.test(raw.dc || '')) { out.door = raw.dc; }
    if (raw.cw) {
      var bits = raw.cw.split('-');
      var widths = [];
      var sum = 0;
      var ok = bits.length >= 2 && bits.length <= 7;
      for (var b = 0; ok && b < bits.length; b++) {
        if (!/^\d+$/.test(bits[b])) { ok = false; break; }
        var bw = parseInt(bits[b], 10);
        if (bw < 1) { ok = false; break; }
        widths.push(bw);
        sum += bw;
      }
      if (ok && sum >= cfg.min_w && sum <= cfg.max_w) { out.widths = widths; }
    }
    return out;
  }

  // Prefill the controls from a decoded share/restore state. Selects only
  // change when a matching option exists, so stale links degrade gracefully.
  function applySharedState(root, st) {
    function setVal(key, v) {
      var el = root.querySelector('[data-cage-input="' + key + '"]');
      if (el) { el.value = v; }
    }
    function setSelect(key, v) {
      var el = root.querySelector('[data-cage-input="' + key + '"]');
      if (!el || !el.options) { return; }
      for (var i = 0; i < el.options.length; i++) {
        if (el.options[i].value === String(v)) { el.selectedIndex = i; return; }
      }
    }
    function setCheck(key) {
      var el = root.querySelector('[data-cage-input="' + key + '"]');
      if (el) { el.checked = true; }
    }
    if (st.widths) {
      setSelect('partitions', st.widths.length - 1);
      setCheck('customwidths');
      var sum = 0;
      for (var i = 0; i < st.widths.length; i++) { sum += st.widths[i]; }
      setVal('w', sum);
    } else {
      if (st.w != null) { setVal('w', st.w); }
      if (st.partitions != null) { setSelect('partitions', st.partitions); }
    }
    if (st.h != null) { setVal('h', st.h); }
    if (st.d != null) { setVal('d', st.d); }
    if (st.doorsPer != null) { setSelect('doorsper', st.doorsPer); }
    if (st.hinge) { setSelect('hinge', st.hinge); }
    if (st.escape) { setSelect('escape', st.escape); }
    if (st.weatherproof) { setCheck('weatherproof'); }
    if (st.topTray) { setCheck('toptray'); }
    if (st.cage) { setColour(root, 'cagecolour', st.cage); }
    if (st.door) { setColour(root, 'doorcolour', st.door); }
  }

  function setColour(root, key, num) {
    var el = root.querySelector('[data-cage-input="' + key + '"]');
    if (!el || !el.options) { return; }
    for (var i = 0; i < el.options.length; i++) {
      var o = el.options[i];
      var code = o.getAttribute ? o.getAttribute('data-code') : '';
      var hit = code ? code.replace(/\D/g, '') === num : (o.value || '').indexOf('RAL ' + num + ' ') === 0;
      if (hit) { el.selectedIndex = i; return; }
    }
  }

  // Copy helper: async clipboard API with a hidden-textarea fallback.
  function copyText(text, done) {
    var nav = global.navigator;
    if (nav && nav.clipboard && nav.clipboard.writeText) {
      nav.clipboard.writeText(text).then(function () { done(true); }, function () { done(legacyCopy(text)); });
      return;
    }
    done(legacyCopy(text));
  }
  function legacyCopy(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      var ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (e) { return false; }
  }

  function selOpt(sel) { return (sel && sel.options) ? sel.options[sel.selectedIndex] : null; }
  function selHex(sel) { var o = selOpt(sel); return o ? (o.getAttribute('data-hex') || '') : ''; }
  // Colour name for the summary/email: built from data attributes so display-only
  // suffixes in the menu (like "(standard)") never reach the quote.
  function selText(sel) {
    var o = selOpt(sel);
    if (!o) { return ''; }
    var code = o.getAttribute('data-code');
    var name = o.getAttribute('data-name');
    if (code) { return name ? code + ' ' + name : code; }
    return (o.textContent || '').replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
  }
  function isCustomColour(sel) {
    var o = selOpt(sel);
    if (!o || !o.getAttribute) { return false; }
    return o.getAttribute('data-standard') === null; // standard option carries data-standard
  }

  function positiveVal(el) {
    if (!el) { return false; }
    var v = parseInt(el.value, 10);
    return !isNaN(v) && v > 0;
  }

  function dimensionsValid(root) {
    if (!positiveVal(root.querySelector('[data-cage-input="h"]'))) { return false; }
    if (!positiveVal(root.querySelector('[data-cage-input="d"]'))) { return false; }
    var customEl = root.querySelector('[data-cage-input="customwidths"]');
    if (customEl && customEl.checked) {
      var cws = root.querySelectorAll('[data-cage-cw]');
      if (!cws.length) { return false; }
      for (var i = 0; i < cws.length; i++) {
        if (!positiveVal(cws[i])) { return false; }
      }
      return true;
    }
    return positiveVal(root.querySelector('[data-cage-input="w"]'));
  }

  function escapeText(c) {
    var comp = (c.compartmentCount != null) ? c.compartmentCount : (c.partitionCount + 1);
    if (c.escapeChoice === 'large') { return 'One large rear escape door'; }
    if (c.escapeChoice === 'each') {
      return comp > 1 ? comp + ' individual rear escape doors (one per compartment)' : 'Twin rear escape doors';
    }
    return 'None';
  }

  function compartmentText(c) {
    var n = (c.compartmentCount != null) ? c.compartmentCount : (c.partitionCount + 1);
    if (c.customWidths && c.compartments) {
      var mm = c.compartments.map(function (x) { return Math.round(x); }).join(', ');
      return n + ' (widths: ' + mm + ' mm)';
    }
    return String(n);
  }

  function doorsText(c) {
    return c.doorsPer + ' per compartment (' + c.doorCount + ' total)';
  }

  function hingesText(c) {
    if (c.hingeChoice === 'left') { return 'All doors hinge left'; }
    if (c.hingeChoice === 'right') { return 'All doors hinge right'; }
    if (c.hingeChoice === 'centre') { return 'Standard (centre hinged)'; }
    return 'Standard (mirrored)';
  }

  function renderSummary(c) {
    var rows = [
      ['Dimensions (W x H x D)', c.w + ' x ' + c.h + ' x ' + c.d + ' mm'],
      ['Compartments', compartmentText(c)],
      ['Doors', doorsText(c)],
      ['Door hinges', hingesText(c)],
      ['Partitions', String(c.partitionCount)],
      ['Weatherproof', c.isWeatherproof ? 'Yes' : 'No'],
      ['Escape door', escapeText(c)],
      ['Top tray', c.hasTopTray ? 'Yes' : 'No'],
      ['Cage colour', c.cageColor || 'Standard'],
      ['Door colour', c.doorColor || 'Standard']
    ];
    return rows.map(function (r) {
      return '<div class="cage-summary__row"><dt>' + r[0] + '</dt><dd>' + r[1] + '</dd></div>';
    }).join('');
  }

  function buildBody(c, priceText, shareUrl) {
    var lines = [
      'BESPOKE CAGE - INITIAL BUILD',
      'Dimensions (W x H x D): ' + c.w + ' x ' + c.h + ' x ' + c.d + ' mm',
      'Compartments: ' + compartmentText(c),
      'Doors: ' + doorsText(c),
      'Door hinges: ' + hingesText(c),
      'Partitions: ' + c.partitionCount,
      'Weatherproof: ' + (c.isWeatherproof ? 'Yes' : 'No'),
      'Escape door: ' + escapeText(c),
      'Top tray: ' + (c.hasTopTray ? 'Yes' : 'No'),
      'Cage colour: ' + (c.cageColor || 'Standard'),
      'Door colour: ' + (c.doorColor || 'Standard'),
      'Estimated price: ' + priceText
    ];
    if (shareUrl) { lines.push('Build link: ' + shareUrl); }
    lines.push('', '(Estimate generated by the online cage builder. Not a final quote.)');
    return lines.join('\n');
  }

  function setHidden(root, c, priceText, shareUrl) {
    function set(name, val) {
      var el = root.querySelector('[data-cage-field="' + name + '"]');
      if (el) { el.value = val; }
    }
    set('Dimensions', c.w + ' x ' + c.h + ' x ' + c.d + ' mm');
    set('Compartments', compartmentText(c));
    set('Doors', doorsText(c));
    set('Door hinges', hingesText(c));
    set('Partitions', String(c.partitionCount));
    set('Weatherproof', c.isWeatherproof ? 'Yes' : 'No');
    set('Escape door', escapeText(c));
    set('Top tray', c.hasTopTray ? 'Yes' : 'No');
    set('Cage colour', c.cageColor || 'Standard');
    set('Door colour', c.doorColor || 'Standard');
    set('Estimated price', priceText);
    set('Build link', shareUrl || '');
    set('body', buildBody(c, priceText, shareUrl));
  }

  function applyLimits(root, cfg) {
    var map = { w: ['min_w', 'max_w', 'def_w'], h: ['min_h', 'max_h', 'def_h'], d: ['min_d', 'max_d', 'def_d'] };
    Object.keys(map).forEach(function (key) {
      var el = root.querySelector('[data-cage-input="' + key + '"]');
      if (!el) { return; }
      el.min = cfg[map[key][0]];
      el.max = cfg[map[key][1]];
      el.step = cfg.step;
      if (!el.value) { el.value = cfg[map[key][2]]; }
    });
  }

  function gateEscape(root, cfg) {
    var sel = root.querySelector('[data-cage-input="escape"]');
    var each = root.querySelector('[data-cage-escape-each]');
    var hint = root.querySelector('[data-cage-escape-hint]');
    var none = root.querySelector('[data-cage-escape-none]');
    var large = root.querySelector('[data-cage-escape-large]');
    var req = root.querySelector('[data-cage-escape-req]');
    if (!sel || !each) { return; }
    var partitions = intVal(root.querySelector('[data-cage-input="partitions"]'), 0);
    var w = intVal(root.querySelector('[data-cage-input="w"]'), cfg.def_w);
    var d = intVal(root.querySelector('[data-cage-input="d"]'), cfg.def_d);
    var twinNeeded = w >= cfg.twin_escape_w;                              // wide: one large rear door is too weak
    var needs = w > cfg.strengthen_over || d > cfg.strengthen_over;       // big: an escape door is required

    // 'each' needs a partition, EXCEPT on a wide single-compartment cage where
    // it means TWIN rear doors and the twin rule forces it.
    var allowEach = partitions >= 1 || twinNeeded;
    each.disabled = !allowEach;
    if (!allowEach && sel.value === 'each') { sel.value = 'large'; }
    if (hint) { hint.hidden = allowEach; }

    if (large) { large.disabled = twinNeeded; }
    if (none) { none.disabled = needs || twinNeeded; }
    if (twinNeeded && sel.value !== 'each') { sel.value = 'each'; }
    else if (needs && sel.value === 'none') { sel.value = 'large'; }

    // Label the 'each' option for what it builds in this state.
    each.textContent = (partitions === 0)
      ? 'Twin escape doors (rear)'
      : 'Individual escape doors (one per compartment)';

    if (req) {
      req.hidden = !(needs || twinNeeded);
      if (twinNeeded) {
        req.textContent = 'Cages ' + cfg.twin_escape_w
          + ' mm or wider need twin or individual rear escape doors for strength.';
      } else if (needs) {
        req.textContent = 'Cages wider or deeper than ' + cfg.strengthen_over
          + ' mm need extra strengthening and a rear escape door.';
      }
    }
  }

  // Doors: every compartment has exactly one door, EXCEPT a single-compartment
  // cage (no partitions), which may have 1 or 2. The doors control only shows
  // when there are no partitions, and the door-width rules gate its options:
  // one door cannot exceed max_door_w, two doors cannot drop under min_door_w.
  function gateDoors(root, cfg) {
    var field = root.querySelector('[data-cage-doors-field]');
    var sel = root.querySelector('[data-cage-input="doorsper"]');
    var note = root.querySelector('[data-cage-doors-note]');
    var single = intVal(root.querySelector('[data-cage-input="partitions"]'), 0) === 0;
    if (field) { field.hidden = !single; }
    if (note) { note.hidden = single; }
    if (!single) { if (sel) { sel.value = '1'; } return; }
    if (!sel) { return; }
    var w = intVal(root.querySelector('[data-cage-input="w"]'), cfg.def_w);
    var oneOk = w <= cfg.max_door_w;
    var twoOk = w / 2 >= cfg.min_door_w;
    var opts = sel.querySelectorAll('option');
    for (var i = 0; i < opts.length; i++) {
      if (opts[i].value === '1') { opts[i].disabled = !oneOk; }
      if (opts[i].value === '2') { opts[i].disabled = !twoOk; }
    }
    if (sel.value === '1' && !oneOk && twoOk) { sel.value = '2'; }
    if (sel.value === '2' && !twoOk && oneOk) { sel.value = '1'; }
  }

  // Top tray is not available on weatherproof cages: hide the control (and clear it)
  // when weatherproof is selected.
  function gateTray(root) {
    var field = root.querySelector('[data-cage-tray-field]');
    var cb = root.querySelector('[data-cage-input="toptray"]');
    var note = root.querySelector('[data-cage-tray-note]');
    var wp = root.querySelector('[data-cage-input="weatherproof"]');
    var weatherproof = !!(wp && wp.checked);
    if (field) { field.hidden = weatherproof; }
    if (note) { note.hidden = !weatherproof; }
    if (weatherproof && cb) { cb.checked = false; }
  }

  // Show/hide and (re)build the per-compartment width inputs when custom sizing
  // is on. Rebuilds only when the compartment count changes, so typing is safe.
  function syncWidthInputs(root, cfg) {
    var wrap = root.querySelector('[data-cage-widths]');
    if (!wrap) { return; }
    var customEl = root.querySelector('[data-cage-input="customwidths"]');
    var custom = !!(customEl && customEl.checked);
    var widthEl = root.querySelector('[data-cage-input="w"]');
    var n = intVal(root.querySelector('[data-cage-input="partitions"]'), 0) + 1;

    wrap.hidden = !custom;
    if (widthEl) { widthEl.disabled = custom; }
    if (!custom) { return; }

    var existing = wrap.querySelectorAll('[data-cage-cw]');
    if (existing.length === n) { return; }

    var old = [];
    for (var i = 0; i < existing.length; i++) { old.push(existing[i].value); }
    var total = intVal(widthEl, cfg.def_w);
    // Split the width into per-compartment defaults WITHOUT changing the total:
    // round down to the step, then hand the remainder out in step-sized chunks
    // (any sub-step leftover goes on the last compartment). The compartments
    // always sum back to the current width, so toggling custom widths on never
    // resizes the cage or the drawing.
    var base = Math.max(cfg.step, Math.floor(total / n / cfg.step) * cfg.step);
    var vals = [];
    for (var v = 0; v < n; v++) { vals.push(base); }
    var rem = total - base * n;
    var vi = 0;
    while (rem >= cfg.step) { vals[vi % n] += cfg.step; rem -= cfg.step; vi++; }
    if (rem > 0) { vals[n - 1] += rem; }
    // Compartment widths double as door widths (1 door per compartment), so the
    // inputs carry the door-width limits; a single compartment keeps the cage limits.
    var cwMin = n > 1 ? cfg.min_door_w : cfg.min_w;
    var cwMax = n > 1 ? cfg.max_door_w : cfg.max_w;
    var html = '';
    for (var k = 0; k < n; k++) {
      var val = (old[k] !== undefined && old[k] !== '') ? old[k] : vals[k];
      html += '<label class="cage-field__label cage-field__label--sub">Compartment ' + (k + 1) + ' width (mm)'
        + '<input class="cage-field__input" type="number" inputmode="numeric" data-cage-cw="' + k + '"'
        + ' min="' + cwMin + '" max="' + cwMax + '" step="' + cfg.step + '" value="' + val + '"></label>';
    }
    wrap.innerHTML = html;
  }

  // Write an inline error message into a slot element (hidden when empty).
  function setError(el, msg) {
    if (!el) { return; }
    el.hidden = !msg;
    var t = el.querySelector ? el.querySelector('[data-cage-error-text]') : null;
    if (t) { t.textContent = msg || ''; } else { el.textContent = msg || ''; }
  }

  function focusQuote(quote) {
    if (!quote) { return; }
    var first = quote.querySelector('input, textarea, select');
    if (quote.scrollIntoView) { quote.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    if (first && first.focus) { first.focus({ preventScroll: true }); }
  }

  function setup(root, cfg) {
    var currency = root.getAttribute('data-currency') || CURRENCY;
    applyLimits(root, cfg);

    var wireframe = root.querySelector('[data-cage-wireframe]');
    var summary = root.querySelector('[data-cage-summary]');
    var priceEls = root.querySelectorAll('[data-cage-price]');
    var submit = root.querySelector('[data-cage-submit]');
    var reveals = root.querySelectorAll('[data-cage-reveal]'); // may appear in more than one place
    var quote = root.querySelector('[data-cage-quote]');
    var swatchCage = root.querySelector('[data-cage-swatch="cage"]');
    var swatchDoor = root.querySelector('[data-cage-swatch="door"]');
    var ri;

    // Shared or remembered build: prefill the fields before the first render.
    var STORE_KEY = 'dogbox-cage-build';
    var loc = (typeof global.location !== 'undefined') ? global.location : null;
    var shareBtn = root.querySelector('[data-cage-share]');
    var shareLabel = root.querySelector('[data-cage-share-label]');
    var shareLabelText = shareLabel ? shareLabel.textContent : '';
    var resetBtn = root.querySelector('[data-cage-reset]');
    var lastQs = '';
    var persistTimer = null;
    var initialState = loc ? decodeParams(loc.search, cfg) : {};
    var hasShared = false;
    for (var sk in initialState) { if (sk) { hasShared = true; break; } }
    if (!hasShared) {
      try { initialState = decodeParams(global.localStorage.getItem(STORE_KEY) || '', cfg); } catch (e) { initialState = {}; }
    }
    applySharedState(root, initialState);

    function setExpanded(v) {
      for (var i = 0; i < reveals.length; i++) { reveals[i].setAttribute('aria-expanded', v ? 'true' : 'false'); }
    }
    function openQuote() { if (quote) { quote.hidden = false; } setExpanded(true); focusQuote(quote); }

    // if the server re-rendered the form open (validation errors), reflect that
    if (quote && !quote.hidden) { setExpanded(true); }
    for (ri = 0; ri < reveals.length; ri++) { reveals[ri].addEventListener('click', openQuote); }

    function setText(els, text) { for (var i = 0; i < els.length; i++) { els[i].textContent = text; } }

    function update() {
      syncWidthInputs(root, cfg);
      gateDoors(root, cfg);
      gateEscape(root, cfg);
      gateTray(root);
      var c = readChoices(root, cfg);
      var widthEl = root.querySelector('[data-cage-input="w"]');
      if (widthEl && widthEl.disabled) { widthEl.value = c.w; } // show summed total
      if (wireframe) { wireframe.innerHTML = buildWireframeSvg(c); }
      if (summary) { summary.innerHTML = renderSummary(c); }
      if (swatchCage) { swatchCage.style.background = c.cageSwatch || ''; }
      if (swatchDoor) { swatchDoor.style.background = c.doorSwatch || ''; }

      // Build rules: inline errors sit directly under the fields that cause
      // them (size errors in the Size card, door errors in the Layout card),
      // and the offending size input is highlighted. The quote is held until
      // the layout is buildable.
      var issue = doorWidthIssue(cfg, c);
      var size = sizeIssue(cfg, {
        w: intVal(widthEl, 0),
        h: intVal(root.querySelector('[data-cage-input="h"]'), 0),
        d: intVal(root.querySelector('[data-cage-input="d"]'), 0)
      });
      var sizeMsg = '';
      if (size) {
        sizeMsg = 'The ' + size.dim + ' is ' + size.value + ' mm. The '
          + (size.type === 'big' ? 'largest' : 'smallest') + ' we can build is ' + size.limit + ' mm.';
      }
      setError(root.querySelector('[data-cage-size-error]'), sizeMsg);
      var dimKeys = { width: 'w', height: 'h', depth: 'd' };
      for (var dk in dimKeys) {
        var dEl = root.querySelector('[data-cage-input="' + dimKeys[dk] + '"]');
        if (dEl && dEl.classList) {
          var bad = !!(size && size.dim === dk);
          dEl.classList.toggle('is-invalid', bad);
          dEl.setAttribute('aria-invalid', bad ? 'true' : 'false');
        }
      }
      // Door-width problems only show once the sizes themselves are in range:
      // a too-big cage is the root cause and gets fixed first.
      var doorMsg = '';
      if (!size && issue) {
        if (issue.type === 'wide') {
          doorMsg = 'A door would be ' + issue.width + ' mm wide, over the '
            + cfg.max_door_w + ' mm maximum. Add a partition or reduce the width.';
        } else {
          var fix = c.doorsPer > 1 ? 'Choose 1 door or increase the width.'
            : (c.partitionCount > 0 ? 'Remove a partition or increase the width.' : 'Increase the width.');
          doorMsg = 'A door would be ' + issue.width + ' mm wide, under the '
            + cfg.min_door_w + ' mm minimum. ' + fix;
        }
      }
      setError(root.querySelector('[data-cage-door-error]'), doorMsg);

      // Keep the address bar, the saved build, and the share button in step
      // with the current configuration. Writes are debounced because Safari
      // rate-limits history.replaceState.
      var qs = encodeChoices(cfg, c);
      lastQs = qs;
      if (resetBtn) { resetBtn.hidden = !qs; }
      if (persistTimer) { clearTimeout(persistTimer); }
      persistTimer = setTimeout(function () {
        try {
          if (loc && global.history && global.history.replaceState) {
            global.history.replaceState(null, '', loc.pathname + (qs ? '?' + qs : ''));
          }
        } catch (e1) { /* ignore: the URL just lags behind */ }
        try { global.localStorage.setItem(STORE_KEY, qs); } catch (e2) { /* private mode */ }
      }, 250);

      var dimsOk = dimensionsValid(root);
      var valid = dimsOk && !issue && !size;
      if (valid) {
        var priceText = currency + formatMoney(computePrice(cfg, c));
        var shareUrl = loc ? loc.origin + loc.pathname + (qs ? '?' + qs : '') : '';
        setText(priceEls, 'Estimated from ' + priceText);
        setHidden(root, c, priceText, shareUrl);
      } else if (!dimsOk) {
        setText(priceEls, 'Enter dimensions to see an estimate');
      } else {
        setText(priceEls, 'Adjust the build to see an estimate');
      }
      if (submit) { submit.disabled = !valid; }
      for (var i = 0; i < reveals.length; i++) { reveals[i].disabled = !valid; }
    }

    if (shareBtn) {
      shareBtn.addEventListener('click', function () {
        var url = loc ? loc.origin + loc.pathname + (lastQs ? '?' + lastQs : '') : '';
        copyText(url, function (copied) {
          if (!shareLabel) { return; }
          shareLabel.textContent = copied ? 'Link copied' : 'Copy failed, use the address bar';
          setTimeout(function () { shareLabel.textContent = shareLabelText; }, 2000);
        });
      });
    }
    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        try { global.localStorage.removeItem(STORE_KEY); } catch (e) { /* private mode */ }
        if (loc) { loc.href = loc.pathname; }
      });
    }

    root.addEventListener('input', update);
    root.addEventListener('change', update);
    update();
    // Custom compartment widths need their inputs to exist first, so a shared
    // build fills them after the first render and then re-renders.
    if (initialState.widths) {
      var cwEls = root.querySelectorAll('[data-cage-cw]');
      for (var wi = 0; wi < cwEls.length && wi < initialState.widths.length; wi++) {
        cwEls[wi].value = initialState.widths[wi];
      }
      update();
    }
  }

  function init() {
    var cfg = resolveConfig(typeof global.CAGE_PRICES === 'string' ? global.CAGE_PRICES : '');
    var roots = document.querySelectorAll('[data-cage-builder]');
    for (var i = 0; i < roots.length; i++) { setup(roots[i], cfg); }
  }

  var api = {
    parseConfig: parseConfig,
    resolveConfig: resolveConfig,
    roundTo: roundTo,
    computePrice: computePrice,
    encodeChoices: encodeChoices,
    decodeParams: decodeParams,
    doorWidthIssue: doorWidthIssue,
    sizeIssue: sizeIssue,
    hingeSide: hingeSide,
    projectBox: projectBox,
    buildWireframeSvg: buildWireframeSvg,
    wireframeLabel: wireframeLabel,
    escapeText: escapeText,
    renderSummary: renderSummary,
    buildBody: buildBody
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  global.CageBuilder = api; // shared with the homepage promo (cage-builder-promo.js)

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }
})(typeof window !== 'undefined' ? window : this);
